import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import { getReviewsDashboard } from "@/api/reviews";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import type {
  ReviewsDashboardDay,
  ReviewsDashboardResponse,
  ReviewsDashboardRow,
} from "@/types/api";

/** Сводка «Сегодня» над очередью отзывов: что пришло и обработано за текущий МСК-день, сколько
 *  ждёт одобрения и сколько — ручного ответа (needs_manual). Данные — тот же `GET /api/reviews/dashboard`, что у дашборда (общий кэш
 *  `["reviews-dashboard"]`). Сегодня — последний из `days` (бэкенд строит их от МСК-«сейчас»).
 *  `received*` = null — DWH недоступен: ячейки «—» и подпись с `dwh_error`, не нули.
 */

type Platform = ReviewsDashboardRow["platform"];

/** Статус очереди, который ставит в фильтры клик по счётчику сводки. */
export type SummaryQueueStatus = "pending" | "needs_manual";

const PLATFORMS: { key: Platform; label: string }[] = [
  { key: "wb", label: "WB" },
  { key: "ozon", label: "Ozon" },
];

type Counts = Pick<
  ReviewsDashboardDay,
  | "received"
  | "received_text"
  | "received_rating_only"
  | "generated"
  | "approved"
  | "published"
>;

interface LegalLine {
  legal_name: string;
  counts: Counts;
}

interface PlatformLine {
  platform: Platform;
  label: string;
  counts: Counts;
  pending: number;
  needsManual: number;
  legals: LegalLine[];
}

type Summary =
  | { ok: true; today: string; lines: PlatformLine[] }
  | { ok: false; error: string };

function sumNullable(a: number | null, b: number | null): number | null {
  return a === null || b === null ? null : a + b;
}

function addCounts(a: Counts, b: Counts): Counts {
  return {
    received: sumNullable(a.received, b.received),
    received_text: sumNullable(a.received_text, b.received_text),
    received_rating_only: sumNullable(
      a.received_rating_only,
      b.received_rating_only,
    ),
    generated: a.generated + b.generated,
    approved: a.approved + b.approved,
    published: a.published + b.published,
  };
}

const ZERO: Counts = {
  received: 0,
  received_text: 0,
  received_rating_only: 0,
  generated: 0,
  approved: 0,
  published: 0,
};

function buildSummary(data: ReviewsDashboardResponse): Summary {
  const today = data.days[data.days.length - 1];
  if (today === undefined)
    return { ok: false, error: "в ответе нет ни одного дня" };
  const lines: PlatformLine[] = [];
  for (const p of PLATFORMS) {
    const rows = data.rows.filter((r) => r.platform === p.key);
    if (rows.length === 0) continue;
    const legals: LegalLine[] = [];
    for (const r of rows) {
      const day = r.daily.find((d) => d.date === today);
      if (day === undefined) {
        return {
          ok: false,
          error: `у ${p.label} · ${r.legal_name} нет данных за ${today}`,
        };
      }
      legals.push({ legal_name: r.legal_name, counts: day });
    }
    lines.push({
      platform: p.key,
      label: p.label,
      counts: legals.reduce((acc, l) => addCounts(acc, l.counts), ZERO),
      pending: rows.reduce((acc, r) => acc + r.queue.pending, 0),
      needsManual: rows.reduce((acc, r) => acc + r.queue.needs_manual, 0),
      legals,
    });
  }
  return { ok: true, today, lines };
}

/** «2026-10-15» → «15.10». Дата календарная (без зоны). */
function formatDayMonth(iso: string): string {
  const [, mm, dd] = iso.split("-");
  return `${dd}.${mm}`;
}

function Cell({
  value,
  bold,
}: {
  value: number | null;
  bold?: boolean;
}): React.ReactElement {
  return (
    <td
      className={cn(
        "px-2 py-1.5 text-right tabular-nums",
        bold && "font-semibold",
        value === null && "font-normal text-muted-foreground",
        value === 0 && "text-muted-foreground/60",
      )}
      title={value === null ? "DWH недоступен" : undefined}
    >
      {value === null ? "—" : value}
    </td>
  );
}

function CountCells({ counts }: { counts: Counts }): React.ReactElement {
  return (
    <>
      <Cell value={counts.received_text} />
      <Cell value={counts.received_rating_only} />
      <Cell value={counts.received} bold />
      <Cell value={counts.generated} />
      <Cell value={counts.approved} />
      <Cell value={counts.published} />
    </>
  );
}

/** Счётчики очереди по площадкам («WB 17 · Ozon 31»), каждый — кнопка-фильтр очереди.
 *  `alertClass` — цвет ненулевого счётчика (то, что нельзя забыть); 0 — приглушён. */
function QueueCounts({
  title,
  status,
  lines,
  count,
  testIdPrefix,
  hint,
  alertClass,
  onShowStatus,
}: {
  title: string;
  status: SummaryQueueStatus;
  lines: PlatformLine[];
  count: (line: PlatformLine) => number;
  testIdPrefix: string;
  hint: string;
  alertClass?: string;
  onShowStatus: (platform: Platform, status: SummaryQueueStatus) => void;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-sm text-muted-foreground">{title}</div>
      <div className="flex items-baseline gap-3">
        {lines.map((line, i) => {
          const n = count(line);
          return (
            <React.Fragment key={line.platform}>
              {i > 0 && <span className="text-muted-foreground">·</span>}
              <button
                type="button"
                data-testid={`${testIdPrefix}-${line.platform}`}
                className="flex items-baseline gap-1.5 rounded px-1 hover:bg-muted"
                title={`Показать в очереди: ${line.label}, ${hint}`}
                onClick={() => onShowStatus(line.platform, status)}
              >
                <span className="text-sm">{line.label}</span>
                <span
                  className={cn(
                    "text-2xl font-semibold tabular-nums",
                    n > 0 && alertClass,
                    n === 0 && alertClass !== undefined && "text-muted-foreground/60",
                  )}
                >
                  {n}
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export function ReviewsTodaySummary({
  onShowStatus,
}: {
  /** Клик по счётчику площадки — поставить в фильтрах очереди её и статус. */
  onShowStatus: (platform: Platform, status: SummaryQueueStatus) => void;
}): React.ReactElement {
  const query = useQuery({
    queryKey: ["reviews-dashboard"],
    queryFn: getReviewsDashboard,
  });
  const [expanded, setExpanded] = useState<Set<Platform>>(new Set());

  if (query.isError) {
    return (
      <div
        role="alert"
        className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
      >
        Сводка недоступна: {extractErrorMessage(query.error, "ошибка запроса")}
      </div>
    );
  }
  if (query.isPending) {
    return (
      <div className="text-sm text-muted-foreground">
        Сводка за сегодня: загрузка…
      </div>
    );
  }

  const data = query.data;
  const summary = buildSummary(data);
  if (!summary.ok) {
    return (
      <div
        role="alert"
        className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
      >
        Сводка недоступна: {summary.error}
      </div>
    );
  }

  const toggle = (p: Platform) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });

  return (
    <div
      data-testid="reviews-today"
      className="flex flex-col gap-2 rounded-md border p-3"
    >
      <div className="text-sm font-semibold">
        Сегодня, {formatDayMonth(summary.today)} (МСК)
      </div>

      {summary.lines.length === 0 ? (
        <div className="text-sm text-muted-foreground">Сегодня данных нет</div>
      ) : (
        <div className="flex flex-wrap items-start gap-4">
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th rowSpan={2} className="px-3 py-1.5 text-left font-medium">
                    Площадка
                  </th>
                  <th colSpan={3} className="px-2 py-1 text-center font-medium">
                    Получено
                  </th>
                  <th
                    rowSpan={2}
                    className="px-2 py-1.5 text-right font-medium"
                  >
                    Сгенерировано
                  </th>
                  <th
                    rowSpan={2}
                    className="px-2 py-1.5 text-right font-medium"
                  >
                    Одобрено
                  </th>
                  <th
                    rowSpan={2}
                    className="px-2 py-1.5 text-right font-medium"
                  >
                    Опубликовано
                  </th>
                </tr>
                <tr className="border-b bg-muted/40 text-xs text-muted-foreground">
                  <th className="px-2 py-1 text-right font-normal">
                    с текстом
                  </th>
                  <th className="px-2 py-1 text-right font-normal">
                    без текста
                  </th>
                  <th className="px-2 py-1 text-right font-normal">итого</th>
                </tr>
              </thead>
              <tbody>
                {summary.lines.map((line) => {
                  const open = expanded.has(line.platform);
                  return (
                    <React.Fragment key={line.platform}>
                      <tr
                        data-testid={`today-${line.platform}`}
                        className="border-t"
                      >
                        <td className="whitespace-nowrap px-3 py-1.5 font-semibold">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 hover:underline"
                            aria-expanded={open}
                            onClick={() => toggle(line.platform)}
                          >
                            {open ? (
                              <ChevronDown className="size-4" />
                            ) : (
                              <ChevronRight className="size-4" />
                            )}
                            {line.label}
                          </button>
                        </td>
                        <CountCells counts={line.counts} />
                      </tr>
                      {open &&
                        line.legals.map((l) => (
                          <tr
                            key={l.legal_name}
                            data-testid={`today-${line.platform}-${l.legal_name}`}
                            className="bg-muted/20 text-muted-foreground"
                          >
                            <td className="whitespace-nowrap py-1 pl-9 pr-3 text-xs">
                              {l.legal_name}
                            </td>
                            <CountCells counts={l.counts} />
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-6">
            <QueueCounts
              title="Ждут одобрения"
              status="pending"
              lines={summary.lines}
              count={(l) => l.pending}
              testIdPrefix="pending"
              hint="ожидают проверки"
              onShowStatus={onShowStatus}
            />
            <QueueCounts
              title="Нужен ручной ответ"
              status="needs_manual"
              lines={summary.lines}
              count={(l) => l.needsManual}
              testIdPrefix="manual"
              hint="нужен ручной ответ"
              alertClass="text-amber-700 dark:text-amber-400"
              onShowStatus={onShowStatus}
            />
          </div>
        </div>
      )}

      {data.dwh_error !== null && (
        <div
          role="alert"
          className="flex items-start gap-2 text-sm font-medium text-red-700 dark:text-red-400"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>«Получено» неизвестно — DWH недоступен: {data.dwh_error}</span>
        </div>
      )}
    </div>
  );
}
